/**
 * Render-time URL sanitizers for tenant-controlled block JSON.
 *
 * Block documents in `pages.blocks` are tenant-authored (seed composers today,
 * a block editor later) and rendered to public visitors, so an href/src
 * carrying a `javascript:`/`data:` scheme would be stored XSS. `CtaSchema.href`
 * and `seo.financingIframeUrl` are scheme-agnostic at the Zod boundary, and the
 * renderer intentionally falls back rather than blanking on bad data — so we
 * sanitize at render time instead of rejecting at parse time.
 */

const ALLOWED_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/**
 * Returns `href` when it is a site-relative path or uses an allowed scheme
 * (http/https/mailto/tel); otherwise `"#"`. Rejects `javascript:`, `data:`,
 * `vbscript:`, and protocol-relative (`//host`) URLs.
 */
export function safeHref(href: string): string {
  // Site-relative paths only ("/x", not protocol-relative "//host").
  if (href.startsWith("/") && !href.startsWith("//")) {
    return href;
  }
  try {
    const url = new URL(href);
    if (ALLOWED_LINK_PROTOCOLS.has(url.protocol)) {
      return href;
    }
  } catch {
    // not a parseable absolute URL — fall through to the safe default
  }
  return "#";
}

/**
 * Returns an https-only iframe src, or `null` for anything else (http,
 * javascript:, data:, unparseable, or undefined). Callers render a placeholder
 * when this returns null.
 */
export function safeIframeSrc(url: string | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:") {
      return url;
    }
  } catch {
    // not a parseable absolute URL
  }
  return null;
}
